import { useState } from "react";
import { Mail, Send, Loader2, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-2xl mx-auto px-4 py-8 sm:py-12 w-full flex flex-col items-center">
      <PageHeader
        icon={Mail}
        eyebrow="We're here for you"
        title="Get in touch"
        subtitle="Questions, feedback, or need a hand? Send us a note and we'll get back to you."
      />

      {sent ? (
        <div data-testid="contact-success" className="w-full max-w-md mx-auto rounded-3xl bg-white border border-border/80 p-8 shadow-sm text-center flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-sage/15 text-sage flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-bronze">Message sent 💛</h2>
          <p className="text-sm text-foreground/75 leading-relaxed">
            Thank you for reaching out. Our team has your message and will reply to{" "}
            <span className="font-medium text-bronze">{form.email}</span> as soon as we can.
          </p>
          <Button
            data-testid="contact-send-another"
            variant="outline"
            className="rounded-full mt-2"
            onClick={() => { setSent(false); setForm({ name: "", email: "", subject: "", message: "" }); }}>
            Send another message
          </Button>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="contact-form" className="w-full max-w-md mx-auto rounded-3xl bg-white border border-border/80 p-6 sm:p-8 shadow-sm space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-name" className="text-sm">Your name</Label>
            <Input id="c-name" data-testid="contact-name" value={form.name} onChange={set("name")}
              placeholder="Jane from St. John's" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-email" className="text-sm">Email</Label>
            <Input id="c-email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")}
              placeholder="you@example.com" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-subject" className="text-sm">Subject <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="c-subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")}
              placeholder="What's this about?" className="rounded-xl" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-message" className="text-sm">Message</Label>
            <Textarea id="c-message" data-testid="contact-message" value={form.message} onChange={set("message")}
              placeholder="Tell us how we can help…" rows={5} className="rounded-xl resize-none" />
          </div>
          <Button type="submit" data-testid="contact-submit" disabled={sending}
            className="w-full rounded-full bg-copper hover:bg-copper/90 text-white min-h-[48px]">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send message</>}
          </Button>
          <p className="text-xs text-muted-foreground text-center pt-1">
            Or email us directly at{" "}
            <a href="mailto:support@haplap.ca" className="text-copper underline">support@haplap.ca</a>
          </p>
        </form>
      )}
    </div>
  );
}
